import Input from '@/components/forms/Input';
import MultiSelect from '@/components/forms/MultiSelect';
import Select from '@/components/forms/Select';
import { useAttributes } from '@/hooks/useAttributes';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { ReactElement, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { closingDateOptions } from '../../store-management.constant';
import { buildTimeOptions } from '../utils/buildTimeOptions';
import { getLastEntryOptions } from '../utils/getLastEntryOptions';

type ClubInfoFormValues = {
  workingTimeFrom: string;
  workingTimeTo: string;
  lastEntryTime: string;
  holidays: string;
  tax: string;
  closingDate: string;
  otherHolidays?: string;
};

type ClubInfoProps = {
  workingHourFromApi?: string;
  workingHourToApi?: string;
  lastEntryFromApi?: string;
  holidaysFromApi?: string;
  closingDateFromApi?: string;
};

export const ClubInfo = ({
  workingHourFromApi,
  workingHourToApi,
  lastEntryFromApi,
  closingDateFromApi,
  holidaysFromApi,
}: ClubInfoProps): ReactElement => {
  const { register, control, setValue, trigger } = useFormContext<ClubInfoFormValues>();
  const { data: holidaysOptions = [] } = useAttributes('holidays') || {};
  const { data: otherHolidaysOptions = [] } = useAttributes('other_holidays');
  const { t } = useDynamicLocale();
  const allTimeOptions = buildTimeOptions();

  const workingFromLocal = useWatch({ control, name: 'workingTimeFrom' });
  const workingFrom = workingFromLocal || workingHourFromApi;
  const workingToLocal = useWatch({ control, name: 'workingTimeTo' });
  const workingTo = workingToLocal || workingHourToApi;
  const lastEntry = useWatch({ control, name: 'lastEntryTime' });

  const lastEntryOptions = getLastEntryOptions(workingFrom!, workingTo!);

  useEffect(() => {
    if (workingHourFromApi) setValue('workingTimeFrom', workingHourFromApi);
    if (workingHourToApi) setValue('workingTimeTo', workingHourToApi);
    if (lastEntryFromApi) setValue('lastEntryTime', lastEntryFromApi);
    if (holidaysFromApi) setValue('holidays', holidaysFromApi);
    if (closingDateFromApi) setValue('closingDate', closingDateFromApi);
  }, []); // runs once on mount

  useEffect(() => {
    if (lastEntryOptions.length === 0) {
      setValue('lastEntryTime', '');
      return;
    }

    const lastOptionValue = lastEntryOptions[lastEntryOptions.length - 1].value;
    const isCurrentValid = !!lastEntry && lastEntryOptions.some((o) => o.value === lastEntry);

    if (!isCurrentValid) {
      setValue('lastEntryTime', lastOptionValue, { shouldDirty: true, shouldValidate: false });
      setTimeout(() => void trigger('lastEntryTime'), 0);
    }
  }, [lastEntryOptions, lastEntry, setValue, trigger]);
  return (
    <div className="flex gap-5 sm:gap-10 md:gap-15 lg:gap-20">
      <div className="w-[200px] xl:w-[245px] shrink-0 ">
        <p className="text-base font-semibold whitespace-nowrap pl-12">
          <Translation id="storeManagement.premiumStores.manageStoreInformation.fieldTitle.clubInfo" />
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
        <Select
          options={allTimeOptions}
          selected={workingHourFromApi}
          forceRequired
          placeholder={t('storeManagement.storeInfo.timePlaceholder')}
          label="storeManagement.basicStores.manageStoreInfo.clubInfo.workingHoursFrom"
          {...register('workingTimeFrom')}
        />

        <Select
          options={allTimeOptions}
          selected={workingHourToApi}
          forceRequired
          placeholder={t('storeManagement.storeInfo.timePlaceholder')}
          label="storeManagement.basicStores.manageStoreInfo.clubInfo.workingHoursTo"
          {...register('workingTimeTo')}
        />

        <Select
          options={lastEntryOptions}
          selected={lastEntryFromApi}
          forceRequired
          placeholder={t('storeManagement.storeInfo.timePlaceholder')}
          label="storeManagement.basicStores.manageStoreInfo.clubInfo.lastEntryTime"
          {...register('lastEntryTime')}
          disabled={lastEntryOptions.length === 0}
        />

        <Select
          options={holidaysOptions}
          selected={holidaysFromApi}
          placeholder="Select"
          label="storeManagement.basicStores.manageStoreInfo.clubInfo.holidays"
          {...register('holidays')}
        />

        <Input
          label="manageStoreInformation.form.description.Tax"
          {...register('tax')}
          placeholder={t('common.form.placeholder.tax')}
        />

        <Select
          options={closingDateOptions}
          selected={closingDateFromApi}
          placeholder="Select"
          label="storeManagement.basicStores.manageStoreInfo.clubInfo.closingDate"
          {...register('closingDate')}
        />

        <MultiSelect
          name="otherHolidays"
          label="storeManagement.basicStores.manageStoreInfo.clubInfo.otherHolidays"
          //@ts-expect-error works but type mismatch
          options={otherHolidaysOptions}
          className="flex"
          placeholder="Select Options"
          emptyIndicator={<p className="text-center text-sm">No results found</p>}
        />
      </div>
    </div>
  );
};
